import React, { useEffect, useMemo, useState } from "react";
import {
    DataGrid,
    GridSortItem,
    GridColumnTypesRecord,
    getGridDefaultColumnTypes,
    GridCellParams,
    GridSortModel,
    GridRowId,
    DEFAULT_GRID_COL_TYPE_KEY,
    GridSelectionModel,
    GridSortDirection,
    GridDensity,
} from "@material-ui/data-grid";
import {
    Box,
    Button,
    Checkbox,
    createStyles,
    Divider,
    Grid,
    Hidden,
    makeStyles,
    Menu,
    Paper,
    Theme,
    useMediaQuery,
    Unstable_TrapFocus as TrapFocus,
    MenuItem,
    TextField,
    ListItemIcon,
    ListItemText,
    FormControl,
    InputLabel,
    Select,
    LinearProgress,
} from "@material-ui/core";
import CustomGridColumnsPanel from "./CustomGridColumnsPanel";
import CustomGridToolbar from "./CustomGridToolbar";
import CustomGridNoRowsOverlay from "./CustomGridNoRowsOverlay";
import CustomGridLoadingOverlay from "./CustomGridLoadingOverlay";
import GridInfo from "./GridInfo";
import CustomGridErrorOverlay from "./CustomGridErrorOverlay";
import MobileTablePagination from "../MobileTablePagination";
import { FORM_ERROR } from "final-form";
import SearchForm, { SearchField } from "./SearchForm";
import CustomGridPagination from "./CustomGridPagination";
import { ACTIONS } from "../../storage/dataReducer";
import { useStore } from "../../storage/StoreProvider";
import ActionBar from "./ActionBar";
import GridColDefExtended from "../../forms/GridColDefExtended";
import { DefaultFetchDataProps } from "../../services/baseService";
import SortIcon from "@material-ui/icons/Sort";
import { FilterOperandType, FilterValue } from "../../models/FilterModel";
import { GridStorage } from "../../storage/browserStorage";
import { useTranslation } from "react-i18next";
import ViewWeekIcon from "@material-ui/icons/ViewWeek";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import PageTitle from "./PageTitle";

// Theme-dependent styles.
// createStyles is a helper function for TS compile.
const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            "& .MuiDataGrid-columnHeaderTitle": {
                overflowWrap: "break-word",
                lineHeight: theme.typography.body1.lineHeight,
                whiteSpace: "pre-wrap",
                textAlign: "center",
                fontWeight: "bold",
            },
            "& .MuiDataGrid-root .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer":
                {
                    width: "auto",
                    visibility: "visible",
                },
            "& .MuiDataGrid-root .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon":
                {
                    opacity: 0.5,
                },
            "& .MuiDataGrid-root .MuiDataGrid-columnHeader, .MuiDataGrid-columnsContainer, .MuiDataGrid-columnSeparator":
                {
                    "min-height": "56px !important",
                },
            "& .MuiDataGrid-row": {
                "min-height": "32px !important",
            },
            "& .MuiDataGrid-root .MuiDataGrid-window": {
                position: "relative !important",
            },
            "& .MuiDataGrid-root.compact .MuiDataGrid-dataContainer": {
                "padding-top": "17px !important",
                "min-height": "-webkit-calc(100% + 17px) !important",
            },
            "& .MuiDataGrid-root.comfortable .MuiDataGrid-dataContainer": {
                "padding-top": "0px !important",
                "min-height": "-webkit-calc(100% - 17px) !important",
            },
            "& .MuiDataGrid-root.compact .MuiDataGrid-footerContainer": {
                "min-height": "-webkit-calc(100% + 17px) !important",
                "padding-top": "17px !important",
            },
            backgroundColor: theme.palette.background.paper,
        },
        spacer: {
            "& .MuiTablePagination-spacer": {
                flex: "1 1",
            },
        },
        button: {
            margin: theme.spacing(1),
            minWidth: 150,
        },
        formControl: {
            margin: theme.spacing(1),
            minWidth: 140,
        },
        mobileGridCell: {
            margin: theme.spacing(2),
        },
        mobileGridCheckbox: {
            margin: theme.spacing(0.5, 0, 0, 0),
        },
        columnMenu: {
            display: "flex",
            flexDirection: "column",
            flex: 1,
            "&:focus": {
                outline: 0,
            },
        },
        menuHeader: {
            padding: 5,
        },
        menuContent: {
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            flex: "1 1",
            maxHeight: 400,
        },
        menuFooter: {
            padding: 4,
            display: "flex",
            justifyContent: "space-between",
        },
        rowsPerPageSelect: {
            height: 40,
        },
    })
);

export function mergeGridColTypes(
    defaultColumnTypes: GridColumnTypesRecord,
    optionsColTypes: GridColumnTypesRecord
): GridColumnTypesRecord {
    const mergedColTypes = { ...defaultColumnTypes, ...optionsColTypes };
    const hydratedOptionColTypes: GridColumnTypesRecord = {};

    Object.entries(mergedColTypes).forEach(
        ([colType, colTypeDef]: [string, any]) => {
            colTypeDef = {
                ...mergedColTypes[
                    colTypeDef.extendType || DEFAULT_GRID_COL_TYPE_KEY
                ],
                ...colTypeDef,
            };
            hydratedOptionColTypes[colType] = colTypeDef;
        }
    );

    return hydratedOptionColTypes;
}

export interface FormDataGridProps {
    defaultColumns: GridColDefExtended[];
    searchFields: SearchField[];
    rows: [];
    rowIdSelector: (rows: any) => string[];
    onFetchData: (props: DefaultFetchDataProps) => Promise<GridData | any>;
    filterValueFormatter: (
        key: string,
        item: any,
        usePreciseSearch: boolean
    ) => any;
    customColumnTypes?: GridColumnTypesRecord;
    loadError: any;
    initialFilter?: any;
    initialSort?: GridSortModel;
    hidePreciseSearch?: boolean;
}

export interface GridData {
    rowsLoading: boolean;
    rows: [];
    rowCount: number;
    error: string;
    validationError: string;
    urls: string;
    pageCount: number;
}

// TODO: Add 10 seconds timeout.
// Add retry button.
export default function FormDataGrid({
    defaultColumns,
    searchFields,
    rows,
    rowIdSelector,
    filterValueFormatter,
    customColumnTypes,
    loadError,
    onFetchData,
    initialFilter,
    initialSort,
    hidePreciseSearch,
}: FormDataGridProps) {
    // CSS style rules.
    const classes = useStyles();
    const useMobileView = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down("sm")
    );
    const storage = GridStorage.getInstance();

    const [initialized, setInitialized] = useState(false);

    const [density, setDensity] = useState<GridDensity>("comfortable");

    // Default grid values.
    const [page, setPage] = useState(storage.getPage() ?? storage.setPage(0));
    const [rowsPerPage, setRowsPerPage] = useState(
        storage.getRowsPerPage() ?? 5
    );
    const [filter, setFilter] = useState(
        initialFilter ?? storage.getSearchFilter() ?? []
    );
    const [resolved, setResolved] = useState(false);
    const [sortModel, setSortModel] = useState<GridSortModel>(
        storage.getSortModel()
            ? storage.getSortModel()
            : initialSort
            ? initialSort
            : []
    );
    const [selectionModel, setSelectionModel] = useState(
        [] as GridSelectionModel
    );
    const rowsPerPageOptions = [5, 10, 20, 50];
    const [anchorEl, setAnchorEl] = React.useState(null);
    const [anchorEl2, setAnchorEl2] = React.useState(null);
    const [searchValue, setSearchValue] = React.useState("");
    const [state, dispatch] = useStore();
    const [columns, setColumns] = useState(defaultColumns);
    const columnsOnMobile = state.columns?.length ? state.columns : columns;
    const { t, i18n } = useTranslation();

    useEffect(() => {
        setColumns(defaultColumns);
    }, [defaultColumns]);

    // API calls / data retrieval.
    // The filter value and rowsPerPage are not watched for changes to exclude unnecessary calls.
    useEffect(() => {
        async function getData() {
            // Request data only if it was not retrieved earlier / not resolved.
            if (!resolved && page === -1) {
                // It will trigger useEffect once again after setting the correct value.
                setPage(0);
            } else if (!resolved) {
                await onFetchData({
                    page: page,
                    rowsPerPage: rowsPerPage,
                    sortModel: sortModel,
                    filter: filter,
                    action: ACTIONS.GET_ROWS,
                });
            } else {
                // To retrieve new data on the next request after validation.
                setResolved(false);
            }
        }

        getData();
        storage.setPage(page);
    }, [page, sortModel, i18n.language]); // eslint-disable-line react-hooks/exhaustive-deps

    // Grid events.
    /**
     * The default search submit handler.
     * @param filterModel  - react-final-from model.
     * @param usePreciseSearch - flag that determines whether to use precise search or not.
     * @returns - FORM_ERROR for react-final-form or void.
     */
    const handleSearch = async (
        filterModel: object,
        usePreciseSearch: boolean
    ) => {
        // TODO: Move to resource file.
        const errorMsg = t("gridInfo:criteriaError");
        if (!Object.keys(filterModel).length) return { [FORM_ERROR]: errorMsg };

        const preciseSearchFields: string[] | undefined =
            storage.getPreciseSearchFields();

        storage.setFilterModel(filterModel);
        // Builds filter query for API dynamically.
        const newFilter = Object.keys(filterModel).flatMap((key) => {
            const value = filterModel[key];
            if (!value) return []; // Removes element, if it is empty.

            if (Array.isArray(value)) {
                if (!value.length) return []; // Removes element, if it is empty.
                return value.flatMap((item) =>
                    filterValueFormatter(
                        key,
                        item,
                        usePreciseSearch ||
                            (preciseSearchFields &&
                                preciseSearchFields.includes(key))
                    )
                );
            }
            return filterValueFormatter(
                key,
                value,
                usePreciseSearch ||
                    (preciseSearchFields && preciseSearchFields.includes(key))
            );
        }) as FilterValue[];

        if (!newFilter.length) return { [FORM_ERROR]: errorMsg };
        storage.setSearchFilter(newFilter);

        // Manually calling fetch data function to catch search form validation messages.
        return await onFetchData({
            page: 0,
            rowsPerPage: rowsPerPage,
            sortModel: sortModel,
            filter: newFilter,
            action: ACTIONS.VALIDATION_ERROR,
        }).then((result) => {
            if (result?.validationError)
                return { [FORM_ERROR]: result.validationError };
            else {
                if (page !== 0) {
                    // Notify useEffect that data is already resolved.
                    setResolved(true);
                    // Reset page.
                    setPage(0);
                }
                setFilter(newFilter);
                // Fill data model with retrieved data after validation.
                dispatch({ type: ACTIONS.ROWS, payload: result });
            }
        });
    };

    const getRowIds = useMemo(() => {
        const currentRows = rows;
        if (currentRows) {
            return rowIdSelector(currentRows) ?? [];
        }
        return [];
    }, [rows]);

    const handleSortModelChange = (model: GridSortModel) => {
        if (JSON.stringify(model) !== JSON.stringify(sortModel)) {
            setSortModel(model);
            storage.setSortModel(model);
        }
    };

    const handlePageChange = (newPage: number) => {
        if (newPage !== page) {
            setPage(newPage);
        }
    };

    const handlePageSizeChange = (pageSize: number) => {
        if (pageSize !== rowsPerPage) {
            setRowsPerPage(pageSize);
            storage.setRowsPerPage(pageSize);
            // Set -1 to trigger useEffect (because current value could be 0).
            page === 0 ? setPage(-1) : setPage(0);
        }
    };

    const handlePageChangeOnMobile = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ) => {
        setPage(newPage - 1);
    };

    const handlePageSizeChangeOnMobile = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ) => {
        const pageSize = parseInt(event.target.value, 10);
        if (pageSize !== rowsPerPage) {
            setRowsPerPage(pageSize);
            storage.setRowsPerPage(pageSize);
            // Set -1 to trigger useEffect (because current value could be 0).
            page === 0 ? setPage(-1) : setPage(0);
        }
    };

    const onRowSelectedHandler = (selectionModel: GridRowId[]) => {
        setSelectionModel(selectionModel);
    };

    const handleSelect = () => {
        const newFilter = selectionModel.map(
            (rowId) =>
                ({
                    Field: "id",
                    Operator: FilterOperandType.Equal,
                    Value1: rowId,
                    Value2: null,
                } as FilterValue)
        );

        setFilter(newFilter);

        // Reset page.
        setPage(-1);
    };

    const FormatValueType = (value: any, type: string) => {
        if (Array.isArray(value)) {
            return value
                .map((arrEl) => {
                    return FormatValueType(arrEl, type);
                })
                .join(" ");
        } else if (typeof value === "object") {
            return "";
        } else {
            return value?.toString();
        }
    };

    // Column menu events for mobile view.
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleOrderByMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl2(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleMenu2Close = () => {
        setAnchorEl2(null);
    };

    const handleSearchValueChange = React.useCallback((event) => {
        setSearchValue(event.target.value);
    }, []);

    const currentColumns = React.useMemo(
        () =>
            !searchValue
                ? columns
                : columns.filter(
                      (column) =>
                          column.field
                              .toLowerCase()
                              .indexOf(searchValue.toLowerCase()) > -1 ||
                          (column.headerName &&
                              column.headerName
                                  .toLowerCase()
                                  .indexOf(searchValue.toLowerCase()) > -1)
                  ),
        [columns, searchValue]
    );

    const toggleAllColumns = React.useCallback(
        (value: boolean) => {
            const columnUpdate = columnsOnMobile.map((column) => ({
                ...column,
                hide: value,
            }));
            dispatch({
                type: ACTIONS.UPDATE_COLUMNS,
                payload: { columns: columnUpdate },
            });

            setColumns(columnUpdate);

            storage.setColumns(
                columnUpdate.filter((c) => !c.hide).map((c) => c.field)
            );
        },
        [columnsOnMobile]
    );

    const toggleDefaultColumns = React.useCallback(
        () => {
            dispatch({
                type: ACTIONS.UPDATE_COLUMNS,
                payload: { columns: defaultColumns },
            });

            const savedColumns = defaultColumns
                .filter((col) => !col.hideByDefault)
                .map((col) => col.field);

            setColumns(defaultColumns);

            storage.setColumns(savedColumns);
        }, // eslint-disable-next-line react-hooks/exhaustive-deps
        [columnsOnMobile]
    );

    const showAllColumns = React.useCallback(
        () => toggleAllColumns(false),
        [toggleAllColumns]
    );
    const hideAllColumns = React.useCallback(
        () => toggleAllColumns(true),
        [toggleAllColumns]
    );
    const loadDefaultColumns = React.useCallback(
        () => toggleDefaultColumns(),
        [toggleDefaultColumns]
    );

    const toggleColumn = (columnIndex: number) => () => {
        const columnUpdate = [...columnsOnMobile];
        columnUpdate[columnIndex].hide = !columnUpdate[columnIndex].hide;
        dispatch({
            type: ACTIONS.UPDATE_COLUMNS,
            payload: { columns: columnUpdate },
        });

        storage.setColumns(
            columnsOnMobile.filter((c) => !c.hide).map((c) => c.field)
        );
    };

    const isColumnSorted = (colName: string, direction: GridSortDirection) => {
        // The MUI Lib MIT license allows only 1 column sort at a time.
        if (sortModel.length === 0) return false;
        return (
            sortModel[0].field === colName && sortModel[0].sort === direction
        );
    };

    const sortColumn =
        (columnIndex: number, direction: GridSortDirection) => () => {
            const col = columnsOnMobile[columnIndex] as GridColDefExtended;
            const newSortModel = [] as GridSortModel;

            if (!isColumnSorted(col.field, direction)) {
                const update = {
                    field: col.field,
                    sort: direction,
                } as GridSortItem;
                newSortModel.push(update);
            }

            setSortModel(newSortModel);
            storage.setSortModel(newSortModel);
            handleMenu2Close();
        };

    useEffect(() => {
        setInitialized(true);
    }, [initialized]);

    // Grid desktop view.
    const displayGridDesktopView = () => {
        // TODO: Need to fix an issue with horizontal scroll appearing on resize.
        return (
            <div>
                <GridInfo rowCount={state?.rowCount ?? 0} />
                <div
                    style={{ minHeight: 488, width: "100%" }}
                    className={classes.root}
                >
                    <div style={{ flexGrow: 1 }}>
                        <DataGrid
                            className={
                                density === "compact"
                                    ? "compact"
                                    : "comfortable"
                            }
                            rows={rows}
                            page={page === -1 ? 1 : page}
                            columns={columns}
                            pageSize={rowsPerPage}
                            onPageSizeChange={handlePageSizeChange}
                            onSelectionModelChange={onRowSelectedHandler}
                            sortModel={initialized ? sortModel : []}
                            disableSelectionOnClick
                            sortingMode="server"
                            onSortModelChange={handleSortModelChange}
                            onPageChange={handlePageChange}
                            loading={state?.rowsLoading}
                            paginationMode="server"
                            rowCount={state?.rowCount}
                            error={loadError && state?.error}
                            rowsPerPageOptions={[5, 10, 20, 50]}
                            autoHeight
                            disableColumnMenu
                            checkboxSelection
                            components={{
                                Toolbar: CustomGridToolbar,
                                NoRowsOverlay: CustomGridNoRowsOverlay,
                                LoadingOverlay: CustomGridLoadingOverlay,
                                ColumnsPanel: CustomGridColumnsPanel,
                                ErrorOverlay: CustomGridErrorOverlay,
                                ColumnUnsortedIcon: ArrowDropDownIcon,
                            }}
                            componentsProps={{
                                pagination: {
                                    ActionsComponent: CustomGridPagination,
                                    className: classes.spacer,
                                    labelRowsPerPage: t("gridInfo:rowsPerPage"),
                                    labelDisplayedRows: ({ from, to, count }) =>
                                        count !== -1
                                            ? t("gridInfo:displayedRows", {
                                                  from: from,
                                                  to: to,
                                                  recordCount: count,
                                              })
                                            : t(
                                                  "gridInfo:displayedRowsNoMoreThan",
                                                  { from: from, to: to }
                                              ),
                                },
                                columnsPanel: {
                                    defaultColumns: defaultColumns,
                                },
                                errorOverlay: {
                                    error: state?.error ?? "",
                                    loadError: loadError ? loadError : "",
                                },
                            }}
                            localeText={{
                                toolbarDensity: t("gridInfo:toolbarDensity"),
                                toolbarDensityLabel: t(
                                    "gridInfo:toolbarDensityLabel"
                                ),
                                toolbarDensityCompact: t(
                                    "gridInfo:toolbarDensityCompact"
                                ),
                                toolbarDensityStandard: t(
                                    "gridInfo:toolbarDensityStandard"
                                ),
                                toolbarDensityComfortable: t(
                                    "gridInfo:toolbarDensityComfortable"
                                ),
                                toolbarColumns: t("gridInfo:toolbarColumns"),
                                footerRowSelected: (rowCount) =>
                                    rowCount !== 1
                                        ? t("gridInfo:footerRowsSelected", {
                                              rowCount:
                                                  rowCount.toLocaleString(),
                                          })
                                        : t("gridInfo:footerRowSelected", {
                                              rowCount:
                                                  rowCount.toLocaleString(),
                                          }),
                                columnsPanelTextFieldLabel: t(
                                    "gridInfo:columnsPanelTextFieldLabel"
                                ),
                                columnsPanelTextFieldPlaceholder: t(
                                    "gridInfo:columnsPanelTextFieldPlaceholder"
                                ),
                                columnsPanelShowAllButton: t(
                                    "gridInfo:columnsPanelShowAllButton"
                                ),
                                columnsPanelHideAllButton: t(
                                    "gridInfo:columnsPanelHideAllButton"
                                ),
                            }}
                            columnTypes={
                                customColumnTypes ? customColumnTypes : null
                            }
                            onStateChange={(v) =>
                                density !== v.state.density.value &&
                                setDensity(v.state.density.value)
                            }
                        />
                    </div>
                </div>
            </div>
        );
    };

    const handleMobileCheckbox = (rowId: number): void => {
        if (isChecked(rowId)) {
            setSelectionModel(
                selectionModel.filter((r) => r !== (rowId as GridRowId))
            );
        } else {
            setSelectionModel([...selectionModel, rowId]);
        }
    };

    const isChecked = (rowId: number): boolean => {
        return selectionModel.includes(rowId);
    };

    // Mobile view.
    const displayGridMobileView = () => {
        return (
            <div>
                <Divider />
                <Box display="flex" justifyContent="center">
                    <GridInfo rowCount={state?.rowCount ?? 0} />
                </Box>
                <Paper variant="outlined">
                    <Grid container>
                        <Grid item container xs={12}>
                            <Grid item xs={6}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.button}
                                    endIcon={<ViewWeekIcon />}
                                    aria-controls="column-select-menu"
                                    aria-haspopup="true"
                                    onClick={handleMenuOpen}
                                >
                                    {t("gridInfo:toolbarColumns")}
                                </Button>
                                <Menu
                                    id="column-select-menu"
                                    anchorEl={anchorEl}
                                    getContentAnchorEl={null}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "center",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "center",
                                    }}
                                    keepMounted
                                    open={Boolean(anchorEl)}
                                    onClose={handleMenuClose}
                                    variant="menu"
                                >
                                    <TrapFocus
                                        open
                                        disableEnforceFocus
                                        isEnabled={() => true}
                                        getDoc={() => document}
                                    >
                                        <div
                                            tabIndex={-1}
                                            className={classes.columnMenu}
                                        >
                                            <div className={classes.menuHeader}>
                                                <MenuItem>
                                                    <TextField
                                                        label={t(
                                                            "gridInfo:columnsPanelTextFieldLabel"
                                                        )}
                                                        placeholder={t(
                                                            "gridInfo:columnsPanelTextFieldPlaceholder"
                                                        )}
                                                        value={searchValue}
                                                        onChange={
                                                            handleSearchValueChange
                                                        }
                                                        variant="standard"
                                                        fullWidth
                                                    />
                                                </MenuItem>
                                            </div>
                                            <div
                                                className={classes.menuContent}
                                            >
                                                {currentColumns.map(
                                                    (column, columnIndex) => {
                                                        const labelId = `checkbox-list-label-${columnIndex}`;
                                                        return (
                                                            <MenuItem
                                                                key={
                                                                    columnIndex
                                                                }
                                                                onClick={toggleColumn(
                                                                    columnIndex
                                                                )}
                                                            >
                                                                <ListItemIcon>
                                                                    <Checkbox
                                                                        color="primary"
                                                                        edge="start"
                                                                        checked={
                                                                            !column.hide
                                                                        }
                                                                        tabIndex={
                                                                            -1
                                                                        }
                                                                        disableRipple
                                                                        inputProps={{
                                                                            "aria-labelledby":
                                                                                labelId,
                                                                        }}
                                                                    />
                                                                </ListItemIcon>
                                                                <ListItemText
                                                                    id={labelId}
                                                                    primary={
                                                                        column.headerName
                                                                    }
                                                                />
                                                            </MenuItem>
                                                        );
                                                    }
                                                )}
                                            </div>
                                            <div className={classes.menuFooter}>
                                                <Button
                                                    onClick={hideAllColumns}
                                                    color="primary"
                                                >
                                                    {t(
                                                        "gridInfo:columnsPanelHideAllButton"
                                                    )}
                                                </Button>
                                                {defaultColumns && (
                                                    <Button
                                                        onClick={
                                                            loadDefaultColumns
                                                        }
                                                        color="primary"
                                                    >
                                                        {t(
                                                            "gridInfo:columnsPanelHideDefaultButton"
                                                        )}
                                                    </Button>
                                                )}
                                                <Button
                                                    onClick={showAllColumns}
                                                    color="primary"
                                                >
                                                    {t(
                                                        "gridInfo:columnsPanelShowAllButton"
                                                    )}
                                                </Button>
                                            </div>
                                        </div>
                                    </TrapFocus>
                                </Menu>
                            </Grid>
                            <Grid item xs={6}>
                                <FormControl
                                    variant="outlined"
                                    className={classes.formControl}
                                >
                                    <InputLabel htmlFor="rows-per-page-dropdown">
                                        {t("gridInfo:rowsPerPage")}
                                    </InputLabel>
                                    <Select
                                        native
                                        value={rowsPerPage}
                                        onChange={handlePageSizeChangeOnMobile}
                                        label={t("gridInfo:rowsPerPage")}
                                        className={classes.rowsPerPageSelect}
                                        inputProps={{
                                            name: "rows-per-page",
                                            id: "rows-per-page-dropdown",
                                        }}
                                    >
                                        {rowsPerPageOptions.map((value) => (
                                            <option value={value}>
                                                {value}
                                            </option>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <Grid item xs={6}>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    className={classes.button}
                                    endIcon={<SortIcon />}
                                    aria-controls="column-order-by-menu"
                                    aria-haspopup="true"
                                    onClick={handleOrderByMenuOpen}
                                >
                                    {t("gridInfo:orderBy")}
                                </Button>
                                <Menu
                                    id="order-by-menu"
                                    anchorEl={anchorEl2}
                                    getContentAnchorEl={null}
                                    anchorOrigin={{
                                        vertical: "bottom",
                                        horizontal: "center",
                                    }}
                                    transformOrigin={{
                                        vertical: "top",
                                        horizontal: "center",
                                    }}
                                    keepMounted
                                    open={Boolean(anchorEl2)}
                                    onClose={handleMenu2Close}
                                    variant="menu"
                                >
                                    {currentColumns.map(
                                        (
                                            column: GridColDefExtended,
                                            columnIndex: number
                                        ) => {
                                            if (!column.sortable) return null;
                                            const labelId = `checkbox-list-label-${columnIndex}`;
                                            return (
                                                <React.Fragment
                                                    key={columnIndex}
                                                >
                                                    {/* Ascending */}
                                                    <MenuItem
                                                        onClick={sortColumn(
                                                            columnIndex,
                                                            "asc"
                                                        )}
                                                    >
                                                        <ListItemIcon>
                                                            <Checkbox
                                                                color="primary"
                                                                edge="start"
                                                                checked={isColumnSorted(
                                                                    column.field,
                                                                    "asc"
                                                                )}
                                                                tabIndex={-1}
                                                                disableRipple
                                                                inputProps={{
                                                                    "aria-labelledby":
                                                                        labelId,
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            id={labelId}
                                                            primary={
                                                                column.headerName
                                                            }
                                                            secondary={t(
                                                                "gridInfo:orderAsc"
                                                            )}
                                                        />
                                                    </MenuItem>
                                                    {/* Descending */}
                                                    <MenuItem
                                                        key={columnIndex}
                                                        onClick={sortColumn(
                                                            columnIndex,
                                                            "desc"
                                                        )}
                                                    >
                                                        <ListItemIcon>
                                                            <Checkbox
                                                                color="primary"
                                                                edge="start"
                                                                checked={isColumnSorted(
                                                                    column.field,
                                                                    "desc"
                                                                )}
                                                                tabIndex={-1}
                                                                disableRipple
                                                                inputProps={{
                                                                    "aria-labelledby":
                                                                        labelId,
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            id={labelId}
                                                            primary={
                                                                column.headerName
                                                            }
                                                            secondary={t(
                                                                "gridInfo:orderDesc"
                                                            )}
                                                        />
                                                    </MenuItem>
                                                </React.Fragment>
                                            );
                                        }
                                    )}
                                </Menu>
                            </Grid>
                            <Grid item xs={6}></Grid>
                        </Grid>
                    </Grid>
                </Paper>

                <Box display="flex" justifyContent="center">
                    <MobileTablePagination
                        rows={rows ? rows.length : 0}
                        totalRows={state?.rowCount ?? 0}
                        count={state?.pageCount ?? 0}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handlePageChangeOnMobile}
                    />
                </Box>
                <Divider />
                {state?.rowsLoading && <LinearProgress />}

                {/* Horizontālo kolonnu renderēšana */}
                {Object.values(rows).map((row: any, rowIndex: number) => {
                    const columnTypes = mergeGridColTypes(
                        getGridDefaultColumnTypes(),
                        customColumnTypes
                    );
                    let colStartIndex;
                    return columnsOnMobile.map((col, colIndex) => {
                        if (col.hide) return null;
                        if (colStartIndex == null) colStartIndex = colIndex;

                        const value = row[col.field];

                        const cellParams: GridCellParams = {
                            id: rowIndex,
                            row: null,
                            hasFocus: false,
                            tabIndex: 0,
                            getValue: (id, field) => row[field],
                            formattedValue: value,
                            value: value,
                            colDef: col,
                            api: null,
                            cellMode: null,
                            field: col.field,
                        };

                        if (col.valueGetter) {
                            cellParams.value = col.valueGetter(cellParams);
                        }

                        if (
                            col.type in columnTypes &&
                            columnTypes[col.type].valueFormatter
                        ) {
                            cellParams.formattedValue = columnTypes[
                                col.type
                            ].valueFormatter({ ...cellParams });
                        }

                        const valueCell = col.renderCell({ ...cellParams });

                        return (
                            <div key={colIndex}>
                                <Grid item container xs>
                                    <Grid item xs={1}>
                                        {colIndex === colStartIndex && (
                                            <Checkbox
                                                checked={isChecked(row.id)}
                                                className={
                                                    classes.mobileGridCheckbox
                                                }
                                                onChange={() =>
                                                    handleMobileCheckbox(row.id)
                                                }
                                                color="primary"
                                                inputProps={{
                                                    "aria-label":
                                                        "primary checkbox",
                                                }}
                                            />
                                        )}
                                    </Grid>
                                    <Grid
                                        item
                                        xs
                                        className={classes.mobileGridCell}
                                    >
                                        {col.headerName}
                                    </Grid>
                                    <Grid
                                        item
                                        xs
                                        className={classes.mobileGridCell}
                                    >
                                        {valueCell}
                                    </Grid>
                                </Grid>
                                <Divider />
                            </div>
                        );
                    });
                })}
            </div>
        );
    };

    return (
        <Box display="flex" flexDirection="column" width="100%">
            <PageTitle />
            <SearchForm
                formFields={searchFields}
                onSubmit={handleSearch}
                hidePreciseSearch={hidePreciseSearch}
            />
            <Box display="flex" flexDirection="row">
                <Box flexGrow={1}>
                    {useMobileView
                        ? displayGridMobileView()
                        : displayGridDesktopView()}
                </Box>
                <Hidden only={["xs", "sm"]}>
                    <Box>
                        <ActionBar ids={getRowIds} func={handleSelect} />
                    </Box>
                </Hidden>
            </Box>
        </Box>
    );
}
